import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { C } from '../theme';
import api from '../api/axios';
import AssistantDock from './AssistantDock';

const NAV_ITEMS = [
  { path: '/dashboard',       label: 'Dashboard',      icon: '📊' },
  { path: '/users',           label: 'User Management', icon: '👤' },
  { path: '/profile/clients', label: 'Clients',        icon: '👥' },
  {
    type: 'group', key: 'profile', label: 'Profile', icon: '🗂️',
    children: [
      { path: '/profile/business',     label: 'Business',     icon: '🏢' },
      { path: '/profile/products',     label: 'Products',     icon: '📦' },
      { path: '/profile/categories',   label: 'Categories',   icon: '📂' },
      { path: '/profile/applications', label: 'Applications', icon: '🚗' },
      { path: '/profile/vendors',      label: 'Vendors',      icon: '🏪' },
    ],
  },
  { path: '/purchase-orders', label: 'Purchase Orders', icon: '🛒' },
];

// Per-role restricted sidebars. Each entry is a nav item's `path`, or a
// group's `key` (e.g. 'inventory'). Order here is the order shown in the
// sidebar. Roles not listed here (admin, manager) see the full admin nav.
const ROLE_NAV = {
  accountant: [
    '/dashboard',
    '/profile/clients',
    '/purchase-orders',
    '/credit-list',
    '/sales-history',
    '/renewal-history',
    '/replacement-history',
  ],
  sales_support: [
    '/dashboard',
    '/profile/clients',
    'inventory',            // Inventory group (dynamic categories)
    '/packages',
    '/product-mapping',
    '/renewal-list',
    '/sales-history',
    '/renewal-history',
    '/replacement-history',
    '/service',
    '/complaint',
  ],
};

export default function Layout({ children, title, navItems = [] }) {
  const { user, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [invCategories, setInvCategories] = useState([]);

  useEffect(() => {
    api.get('/api/categories').then(r => setInvCategories(r.data)).catch(() => {});
  }, []);

  const inventoryGroup = {
    type: 'group', key: 'inventory', label: 'Inventory', icon: '📦',
    children: invCategories.map(c => ({
      path: `/inventory/${c.id}`,
      label: c.name,
      icon: '📂',
    })),
  };

  const allNav = [
    ...NAV_ITEMS,
    inventoryGroup,
    { path: '/packages', label: 'Package', icon: '🎁' },
    { path: '/product-mapping', label: 'Product Mapping', icon: '🗺️' },
    { path: '/dealers',         label: 'Dealers',         icon: '🤝' },
    { path: '/dealer-mapping',  label: 'Dealer Mapping',  icon: '🗂️' },
    { path: '/credit-list',    label: 'Credit List',     icon: '💳' },
    { path: '/renewal-list',   label: 'Renewal List',    icon: '🔄' },
    { path: '/sales-history',   label: 'Sales History',    icon: '📈' },
    { path: '/renewal-history',     label: 'Renewal History',     icon: '🔁' },
    { path: '/replacement-history', label: 'Replacement History', icon: '🔀' },
    { path: '/service',            label: 'Service',             icon: '🔧' },
    { path: '/complaint',          label: 'Complaints',          icon: '📢' },
    ...navItems,
  ];

  // Restrict the sidebar for roles listed in ROLE_NAV: keep only the allowed
  // items (matched by path or group key), in the configured order.
  const roleMenu = ROLE_NAV[user?.role];
  const visibleNav = roleMenu
    ? roleMenu
        .map(id => allNav.find(item => item.path === id || item.key === id))
        .filter(Boolean)
    : allNav;

  // Auto-open any group whose child matches the current path
  const defaultOpen = {};
  visibleNav.forEach(item => {
    if (item.type === 'group' && item.children?.some(c => location.pathname.startsWith(c.path))) {
      defaultOpen[item.key] = true;
    }
  });
  const [openGroups, setOpenGroups] = useState(defaultOpen);

  const toggleGroup = (key) => setOpenGroups(p => ({ ...p, [key]: !p[key] }));

  const handleLogout = () => { logout(); navigate('/login'); };

  const sidebarW = collapsed ? '64px' : '230px';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Segoe UI', sans-serif" }}>

      {/* ── Sidebar ── */}
      <aside style={{
        width: sidebarW, minWidth: sidebarW, background: C.sidebar,
        display: 'flex', flexDirection: 'column',
        position: 'sticky', top: 0, height: '100vh',
        transition: 'width 0.2s ease', overflow: 'hidden',
        zIndex: 100,
      }}>

        {/* Logo row */}
        <div style={{
          padding: collapsed ? '20px 0' : '22px 20px',
          borderBottom: `1px solid ${C.sidebarBorder}`,
          display: 'flex', alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          flexShrink: 0,
        }}>
          {!collapsed && (
            <div>
              <div style={{ fontSize: '18px', fontWeight: 800, color: C.primary, letterSpacing: '1px', whiteSpace: 'nowrap' }}>
                🐝 HIVE ERP
              </div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)', letterSpacing: '3px', marginTop: '2px' }}>
                ENTERPRISE PLATFORM
              </div>
            </div>
          )}
          {collapsed && <span style={{ fontSize: '20px' }}>🐝</span>}
          <button
            onClick={() => setCollapsed(p => !p)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(255,255,255,0.35)', fontSize: '16px', padding: '4px',
              flexShrink: 0,
            }}
          >
            {collapsed ? '▶' : '◀'}
          </button>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '10px 0', overflowY: 'auto' }}>
          {visibleNav.map((item, i) => {

            // ── Collapsible group ──
            if (item.type === 'group') {
              const isOpen    = !!openGroups[item.key];
              const anyActive = item.children?.some(c => location.pathname === c.path);
              const childH    = item.children.length * 40; // approx max-height for transition

              return (
                <div key={item.key}>
                  {/* Group header */}
                  <div
                    onClick={() => !collapsed && toggleGroup(item.key)}
                    title={collapsed ? item.label : undefined}
                    style={{
                      display: 'flex', alignItems: 'center',
                      gap: collapsed ? 0 : '10px',
                      padding: collapsed ? '12px 0' : '11px 20px',
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      cursor: collapsed ? 'default' : 'pointer',
                      fontSize: '13px',
                      fontWeight: anyActive ? 700 : 400,
                      color: anyActive ? C.primary : 'rgba(255,255,255,0.65)',
                      background: isOpen && !collapsed
                        ? 'rgba(245,166,35,0.07)'
                        : anyActive && collapsed ? C.sidebarActive : 'transparent',
                      borderRight: anyActive && collapsed ? `3px solid ${C.primary}` : '3px solid transparent',
                      transition: 'background 0.2s, color 0.2s',
                      userSelect: 'none',
                    }}
                    onMouseEnter={e => { if (!collapsed) e.currentTarget.style.background = C.sidebarHover; }}
                    onMouseLeave={e => {
                      if (!collapsed)
                        e.currentTarget.style.background = isOpen ? 'rgba(245,166,35,0.07)' : 'transparent';
                    }}
                  >
                    <span style={{ fontSize: '15px', flexShrink: 0 }}>{item.icon}</span>
                    {!collapsed && (
                      <>
                        <span style={{ whiteSpace: 'nowrap', flex: 1 }}>{item.label}</span>
                        {/* Animated chevron */}
                        <svg
                          width="12" height="12" viewBox="0 0 12 12" fill="none"
                          style={{
                            transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            flexShrink: 0,
                          }}
                        >
                          <path d="M2 4l4 4 4-4" stroke="rgba(255,255,255,0.4)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </>
                    )}
                  </div>

                  {/* Children — always mounted, animated via max-height + opacity */}
                  {!collapsed && (
                    <div style={{
                      overflow: 'hidden',
                      maxHeight: isOpen ? `${childH + 16}px` : '0px',
                      opacity:   isOpen ? 1 : 0,
                      transition: 'max-height 0.28s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease',
                    }}>
                      <div style={{
                        margin: '2px 12px 6px 20px',
                        borderLeft: `1.5px solid rgba(245,166,35,0.2)`,
                        borderRadius: '0 0 0 4px',
                      }}>
                        {item.children.map(child => {
                          const active = location.pathname === child.path;
                          return (
                            <div
                              key={child.path}
                              onClick={() => navigate(child.path)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '9px',
                                padding: '8px 12px 8px 14px',
                                cursor: 'pointer', fontSize: '12.5px',
                                fontWeight: active ? 600 : 400,
                                color: active ? C.primary : 'rgba(255,255,255,0.5)',
                                background: active ? C.sidebarActive : 'transparent',
                                borderRight: active ? `3px solid ${C.primary}` : '3px solid transparent',
                                borderRadius: '0 6px 6px 0',
                                transition: 'background 0.15s, color 0.15s',
                              }}
                              onMouseEnter={e => { if (!active) { e.currentTarget.style.background = C.sidebarHover; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; } }}
                              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; } }}
                            >
                              <span style={{ fontSize: '13px', flexShrink: 0 }}>{child.icon}</span>
                              <span style={{ whiteSpace: 'nowrap' }}>{child.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // ── Regular nav item ──
            const active = location.pathname === item.path;
            return (
              <div
                key={item.path}
                onClick={() => navigate(item.path)}
                title={collapsed ? item.label : undefined}
                style={{
                  display: 'flex', alignItems: 'center',
                  gap: collapsed ? 0 : '10px',
                  padding: collapsed ? '12px 0' : '11px 20px',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  cursor: 'pointer', fontSize: '13px',
                  fontWeight: active ? 600 : 400,
                  color: active ? C.primary : 'rgba(255,255,255,0.6)',
                  background: active ? C.sidebarActive : 'transparent',
                  borderRight: active ? `3px solid ${C.primary}` : '3px solid transparent',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = C.sidebarHover; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: '15px', flexShrink: 0 }}>{item.icon}</span>
                {!collapsed && <span style={{ whiteSpace: 'nowrap' }}>{item.label}</span>}
              </div>
            );
          })}
        </nav>

        {/* User strip */}
        <div style={{
          borderTop: `1px solid ${C.sidebarBorder}`,
          padding: collapsed ? '14px 0' : '14px 16px',
          flexShrink: 0,
        }}>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <div style={{
                width: '34px', height: '34px', borderRadius: '50%',
                background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, color: '#fff', fontSize: '13px', flexShrink: 0,
              }}>
                {(user?.username || 'A')[0].toUpperCase()}
              </div>
              <div style={{ overflow: 'hidden' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.username || 'Admin'}
                </div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', textTransform: 'capitalize' }}>
                  {user?.role || 'admin'}
                </div>
              </div>
            </div>
          )}
          {collapsed && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
              <div style={{
                width: '30px', height: '30px', borderRadius: '50%',
                background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, color: '#fff', fontSize: '12px',
              }}>
                {(user?.username || 'A')[0].toUpperCase()}
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            title="Sign Out"
            style={{
              width: '100%', padding: '7px 10px',
              background: 'rgba(255,255,255,0.06)',
              color: 'rgba(255,255,255,0.5)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '6px', cursor: 'pointer',
              fontSize: '11px', fontWeight: 500,
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: '6px',
            }}
          >
            <span>⏻</span>
            {!collapsed && 'Sign Out'}
          </button>
        </div>
      </aside>

      {/* ── Content ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.primaryBg, minWidth: 0 }}>

        {/* Topbar */}
        <header style={{
          background: C.white,
          borderBottom: `1px solid ${C.border}`,
          padding: '0 28px',
          height: '56px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 50, flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '3px', height: '20px', background: C.primary, borderRadius: '2px' }} />
            <h1 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: C.textMain }}>{title}</h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.textSub }}>
            <span style={{ fontSize: '13px' }}>📅</span>
            {(() => { const d = new Date(); return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`; })()}
          </div>
        </header>

        {/* Page body — bottom padding leaves room for the docked assistant bar */}
        <main style={{ flex: 1, padding: '24px 28px 76px', overflow: 'auto' }}>
          {children}
        </main>
      </div>

      {/* Global draggable assistant (admin frame) */}
      <AssistantDock />
    </div>
  );
}
